import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';
import { useSwipeSteppedCarousel } from '../hooks/useSwipeSteppedCarousel';
import {
  fetchSharedAnswerExamples,
  type SharedAnswerExample,
} from '../services/share';
import { buildPublicSharePath } from '../utils/shareRoute';
import { splitTextPreview } from '../utils/text';
import { FadeSwitch } from './FadeSwitch';
import { LandingDataPlaceholder } from './LandingDataPlaceholder';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import { ProfilePhotoStack } from './ui/ProfilePhotoStack';
import { User } from './Icons';
import { useNearViewport } from '../hooks/useNearViewport';

const QUESTION_LEAD_MAX_LENGTH = 44;
const QUESTION_PREVIEW_MAX_LENGTH = 180;
const INITIAL_EXAMPLES_REFRESH_DELAY_MS = 3_000;

interface LandingQuestionExamplesProps {
  initialExamples?: SharedAnswerExample[];
  sectionId?: string;
  visibleSections: Set<string>;
  loadExamples?: typeof fetchSharedAnswerExamples;
  onAvailabilityChange?: (hasExamples: boolean) => void;
}

export function LandingQuestionExamples({
  initialExamples = [],
  sectionId = 'how-it-works',
  visibleSections,
  loadExamples = fetchSharedAnswerExamples,
  onAvailabilityChange,
}: LandingQuestionExamplesProps) {
  const { language, t } = useTranslation();
  const [examples, setExamples] = useState(initialExamples);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'unavailable'>(
    initialExamples.length > 0 ? 'ready' : 'loading',
  );
  const requestedLanguageRef = useRef(language);
  const carouselGesture = useSwipeSteppedCarousel();
  const { elementRef: sectionRef, isNearViewport } = useNearViewport<HTMLElement>();

  useEffect(() => {
    let isCurrentRequest = true;

    if (!isNearViewport) {
      return () => {
        isCurrentRequest = false;
      };
    }

    const languageChanged = language !== requestedLanguageRef.current;
    requestedLanguageRef.current = language;

    const refreshExamples = (showLoader: boolean) => {
      if (showLoader) setLoadState('loading');

      void loadExamples(language)
        .then((loadedExamples) => {
          if (isCurrentRequest) {
            setExamples(loadedExamples);
            setLoadState(loadedExamples.length > 0 ? 'ready' : 'unavailable');
          }
        })
        .catch(() => {
          if (isCurrentRequest) {
            setExamples([]);
            setLoadState('unavailable');
          }
        });
    };

    if (languageChanged) {
      refreshExamples(true);
      return () => {
        isCurrentRequest = false;
      };
    }

    const timeoutId = window.setTimeout(
      () => refreshExamples(examples.length === 0),
      examples.length === 0 ? 0 : INITIAL_EXAMPLES_REFRESH_DELAY_MS,
    );

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(timeoutId);
    };
  }, [examples.length, isNearViewport, language, loadExamples]);

  useEffect(() => {
    onAvailabilityChange?.(examples.length > 0);
  }, [examples.length, onAvailabilityChange]);

  return (
    <section ref={sectionRef} id={sectionId} className={landingSectionClassName({ className: 'px-4 py-14 sm:px-6 sm:py-32' })}>
      <div className="mx-auto w-full max-w-[96rem]">
        <div
          id="how-header"
          data-landing-scroll-target
          data-animate
          className={`mb-2 text-center transition-all duration-1000 sm:mb-3 ${
            visibleSections.has('how-header') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <h2 className={LANDING_SECTION_TITLE_CLASS_NAME}>
            <span className="font-normal">{t('howItWorks.titlePrefix')}</span>
            <strong className="block font-black">{t('howItWorks.titleEmphasis')}</strong>
          </h2>
          <ProfilePhotoStack className="my-4 sm:my-5" />
          <p className="theme-subtitle mx-auto max-w-2xl text-base sm:text-xl">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        <FadeSwitch
          viewKey={loadState}
          className="relative min-h-[27rem] min-[900px]:min-h-[35rem]"
          childClassName="min-h-[27rem] min-[900px]:min-h-[35rem]"
        >
          {loadState === 'loading' ? (
            <LandingDataPlaceholder
              className="min-h-[27rem] min-[900px]:min-h-[35rem]"
              label={t('howItWorks.loading')}
              testId="question-examples-loader"
            />
          ) : loadState === 'unavailable' ? (
            <LandingDataPlaceholder
              className="min-h-[27rem] min-[900px]:min-h-[35rem]"
              label={t('howItWorks.unavailable')}
              loading={false}
              testId="question-examples-unavailable"
            />
          ) : (
            <div
              id="how-examples"
              data-animate
              className={`pb-8 pt-2 transition-all duration-1000 sm:pb-12 sm:pt-2 ${
                visibleSections.has('how-examples') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <div
                className={`question-examples-carousel ${carouselGesture.isInteracting ? 'question-examples-carousel-interacting' : ''}`}
                data-testid="question-examples-carousel"
                onClickCapture={carouselGesture.onClickCapture}
                onPointerCancel={carouselGesture.onPointerCancel}
                onPointerDown={carouselGesture.onPointerDown}
                onPointerLeave={carouselGesture.onPointerLeave}
                onPointerMove={carouselGesture.onPointerMove}
                onPointerUp={carouselGesture.onPointerUp}
              >
                <div ref={carouselGesture.trackRef} className="question-examples-carousel-track flex w-max">
                  {[false, true].map((isDuplicate) => (
                    <div
                      key={isDuplicate ? 'duplicates' : 'originals'}
                      className="flex gap-10 px-5 sm:gap-14 sm:px-7"
                      data-carousel-group={isDuplicate ? 'duplicate' : 'original'}
                    >
                      {examples.map((example) => (
                        <a
                          key={example.slug}
                          href={buildPublicSharePath(example.slug)}
                          target="_blank"
                          rel="noopener noreferrer"
                          tabIndex={isDuplicate ? -1 : undefined}
                          aria-hidden={isDuplicate ? true : undefined}
                          aria-label={`${t('howItWorks.openSharedQuestion')} : ${example.question}`}
                          data-carousel-copy={isDuplicate ? 'duplicate' : 'original'}
                          className="brand-focus-ring group flex h-[22rem] w-[82vw] max-w-[30rem] shrink-0 flex-col items-center justify-center rounded-[2rem] p-6 text-center transition-transform duration-300 hover:scale-[1.015] focus-visible:scale-[1.015] motion-reduce:transition-none sm:w-[30rem] sm:p-10 min-[900px]:h-[30rem] lg:w-[34rem]"
                        >
                          <QuestionCardContent example={example} />
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </FadeSwitch>
      </div>
    </section>
  );
}

function QuestionCardContent({
  example,
}: {
  example: SharedAnswerExample;
}) {
  const { language, t } = useTranslation();
  const rating = normalizeRating(example.rating);
  const questionPreview = splitTextPreview(
    example.question,
    QUESTION_LEAD_MAX_LENGTH,
    QUESTION_PREVIEW_MAX_LENGTH,
  );
  const localizedRating = rating?.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
    maximumFractionDigits: 1,
  });

  return (
    <span className="relative grid h-full w-full grid-rows-[4rem_minmax(0,1fr)_2.5rem] overflow-hidden sm:grid-rows-[4.5rem_minmax(0,1fr)_3rem]">
      <span
        aria-hidden="true"
        data-anonymous-user-background
        className="pointer-events-none absolute -bottom-8 -right-5 text-[rgb(var(--theme-primary))] sm:-bottom-10 sm:-right-6"
      >
        <User className="h-40 w-40 stroke-[0.8] opacity-[0.08] transition-opacity duration-300 group-hover:opacity-[0.13] group-focus-visible:opacity-[0.13] motion-reduce:transition-none sm:h-52 sm:w-52" />
      </span>
      <span className="relative z-10 flex items-start justify-center">
        {example.customHeading && (
          <span className="line-clamp-2 max-w-[17rem] self-center text-left text-xs font-black uppercase tracking-[0.18em] text-[rgb(var(--theme-primary-strong))] sm:text-sm">
            {example.customHeading}
          </span>
        )}
      </span>
      <span data-question-preview className="relative z-10 min-h-0 break-words pt-3 text-center sm:pt-4">
        <span
          data-question-lead
          className="theme-heading-primary text-3xl font-black leading-[1.02] tracking-[-0.045em] sm:text-4xl lg:text-5xl"
          title={example.question}
        >
          {questionPreview.lead}
        </span>
        {questionPreview.continuation && (
          <span data-question-continuation className="theme-subtitle text-sm font-semibold leading-snug sm:text-base">
            {' '}{questionPreview.continuation}
          </span>
        )}
      </span>
      <span className="relative z-10 flex items-end justify-center">
        {rating !== null && (
          <span
            data-testid="question-rating"
            className="flex items-center justify-center gap-1 text-amber-500"
            aria-label={t('rating.multipleStars').replace('{count}', localizedRating ?? '')}
          >
            {Array.from({ length: 5 }, (_, index) => (
              <Star
                key={index}
                aria-hidden="true"
                className="h-5 w-5"
                fill={index < Math.round(rating) ? 'currentColor' : 'none'}
              />
            ))}
            <span className="ml-2 text-sm font-black tabular-nums text-[rgb(var(--theme-text))]">
              {localizedRating} / 5
            </span>
          </span>
        )}
      </span>
    </span>
  );
}

function normalizeRating(rating: number | null | undefined): number | null {
  if (rating === undefined || rating === null || !Number.isFinite(rating) || rating <= 0) return null;
  return Math.min(rating, 5);
}
